"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Download, Lock, Check, ArrowRight } from "lucide-react";

export default function LoyaltyOwnershipScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Your customer relationship", "Tu relación con el cliente")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Captured at your counter. ", "Capturados en tu mostrador. ")}
            <span className="text-redy-charcoal/30">{t("Not on a marketplace.", "No en un marketplace.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Every phone number and visit is captured at your own checkout and on your own page — not rented from a marketplace app. You see who comes back, how often and what they spend, and you reward them from the same place you run the business.",
              "Cada teléfono y cada visita se capturan en tu propio checkout y en tu propia página — no se alquilan a una app de marketplace. Ves quién vuelve, cada cuánto y cuánto gasta, y los premias desde el mismo lugar donde manejas el negocio."
            )}
          </p>
        </motion.div>

        {/* Comparison cards */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[860px] mx-auto"
        >
          {/* Marketplace app — generic, no specific competitor */}
          <div className="bg-white border border-black/5 rounded-[24px] p-7 shadow-[0_20px_60px_rgba(0,0,0,0.04)]">
            <div className="flex items-center gap-2.5 mb-5">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "rgba(220,38,38,0.10)" }}
              >
                <Lock className="w-4 h-4" style={{ color: "#DC2626" }} />
              </div>
              <div>
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                  {t("Marketplace app", "App de marketplace")}
                </div>
                <div className="text-[16px] font-black text-redy-charcoal">{t("Rented reach", "Alcance alquilado")}</div>
              </div>
            </div>

            <ul className="space-y-2.5 text-[13px] font-medium text-redy-secondary mb-5">
              <li className="flex items-start gap-2">
                <span className="text-redy-secondary/40 mt-0.5">—</span>
                <span>{t("Orders go through the app's checkout, under its brand", "Las órdenes pasan por el checkout de la app, con su marca")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-redy-secondary/40 mt-0.5">—</span>
                <span>{t("Commission of up to 30% on each order", "Comisión de hasta 30% en cada orden")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-redy-secondary/40 mt-0.5">—</span>
                <span>{t("Contact details depend on what the app shares", "Los datos de contacto dependen de lo que la app comparta")}</span>
              </li>
            </ul>

            <div className="text-center text-[12px] font-bold text-redy-secondary/55 italic">
              {t("\"Great for discovery. Pricey for regulars.\"", "\"Bueno para descubrimiento. Caro para los de siempre.\"")}
            </div>
          </div>

          {/* Redy — open */}
          <div
            className="rounded-[24px] p-7 shadow-2xl text-white relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1A1A1A 0%, #2A1A1A 100%)" }}
          >
            <div
              className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(244,114,43,0.18) 0%, transparent 65%)" }}
            />
            <div className="relative">
              <div className="flex items-center gap-2.5 mb-5">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(244,114,43,0.20)" }}
                >
                  <Download className="w-4 h-4" style={{ color: "#F4722B" }} />
                </div>
                <div>
                  <div className="text-[10px] font-black tracking-widest uppercase" style={{ color: "#F4722B" }}>
                    Redy
                  </div>
                  <div className="text-[16px] font-black text-white">{t("Your regulars, in one place", "Tus clientes, en un solo lugar")}</div>
                </div>
              </div>

              <ul className="space-y-2.5 text-[13px] font-medium text-white/75 mb-5">
                {[
                  t("Customer profiles with visits, total spent and last visit", "Perfil de cada cliente con visitas, gasto total y última visita"),
                  t("Points and rewards tied to their phone number", "Puntos y premios atados a su número de teléfono"),
                  t("SMS opt-in tracked for every customer", "Opt-in de SMS registrado por cliente"),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: "#2ECC71" }} strokeWidth={3} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div
                className="rounded-[12px] px-4 py-3 flex items-center justify-between"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                }}
              >
                <div className="text-[12px] font-bold text-white">
                  {t("Admin → Customers", "Admin → Clientes")}
                </div>
                <ArrowRight className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
